import { useCallback, useEffect, useMemo, useState } from "react";
import {
    useNodesState,
    useEdgesState,
    Node,
    Edge,
    addEdge,
    ConnectionLineType,
    useReactFlow,
} from "reactflow";
import { EdgeType, GraphDirection, NodeType } from "../models/common";
import { ButtonsGroup, CustomEdge } from "../components";
import { getLayoutedElements } from "../utils/alignNodes";
import "reactflow/dist/style.css";

export interface UseGraphProps {
    initialEdges: Edge[];
    initialNodes: Node[];
}

const useGraph = ({ initialEdges, initialNodes }: UseGraphProps) => {
    const { setCenter, zoomTo } = useReactFlow();
    const nodeTypes = useMemo(
        () => ({ [NodeType.TopicPair]: ButtonsGroup }),
        []
    );
    const edgeTypes = useMemo(() => ({ [EdgeType.Custom]: CustomEdge }), []);
    const [nodes, setNodes, onNodesChange] = useNodesState([]);
    const [edges, setEdges, onEdgesChange] = useEdgesState([]);
    const [aligned, setAligned] = useState(false);

    const connect = useCallback(
        (params: any) => {
            setEdges((eds: Edge[]) =>
                addEdge(
                    {
                        ...params,
                        type: ConnectionLineType.Bezier,
                        animated: true,
                    },
                    eds
                )
            );
        },
        [setEdges]
    );

    const alignAndFitNodes = useCallback(
        (direction?: GraphDirection) => {
            if (!nodes.length || !edges.length) return;
            window.requestAnimationFrame(() => {
                const { nodes: layoutedNodes, edges: layoutedEdges } =
                    getLayoutedElements(nodes, edges, direction);

                setNodes([...layoutedNodes]);
                setEdges([...layoutedEdges]);
                setAligned(true);
                const parentNode = layoutedNodes?.[0];
                if (parentNode) {
                    setCenter(parentNode?.position?.x, parentNode?.position?.y);
                    zoomTo(0);
                }
            });
        },
        [nodes, edges, setNodes, setEdges, setCenter, zoomTo]
    );

    useEffect(() => {
        if (initialNodes.length) {
            setNodes(initialNodes);
            setEdges(initialEdges);
            setAligned(false);
        }
    }, [initialEdges, initialNodes, setEdges, setNodes]);

    useEffect(() => {
        if (!aligned && nodes.length && edges.length) {
            alignAndFitNodes();
        }
    }, [alignAndFitNodes, aligned, edges.length, nodes.length]);

    return {
        nodeTypes,
        edgeTypes,
        nodes,
        edges,
        aligned,
        onNodesChange,
        onEdgesChange,
        connect,
        alignAndFitNodes,
    };
};

export default useGraph;
